import { Box, Typography, Paper, Grid, Card, CardContent, Avatar, Chip, Button, List, ListItem, ListItemAvatar, ListItemText, Divider, Container, Alert, LinearProgress } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useAppSelector } from '../hooks/redux'
import { useHasRole } from '../hooks/usePermission'
import { useNavigate } from 'react-router-dom'
import {
  Folder,
  Groups,
  People,
  Description,
  Assessment,
  VideoCall,
  TrendingUp,
  Notifications,
  Schedule,
  ArrowForward,
  AttachFile,
  BarChart,
  CalendarToday,
  AccessTime,
  CheckCircle,
  ErrorOutline,
  SmartToy,
  EmojiEvents,
  WarningAmber,
  HourglassEmpty,
} from '@mui/icons-material'
import { gradientBrand } from '../theme/brand'
import NextHearingCountdown from '../components/NextHearingCountdown'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetReportsDashboardQuery, useGetPublicLatestResultsQuery } from '../store/api/reportsApi'
import { useGetMemorialsQuery } from '../store/api/memorialsApi'
import { useGetNotificationsQuery } from '../store/api/notificationsApi'
import { getPriorityRegistrationIssues, getTeamRegistrationSummary } from '../utils/teamRegistration'

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'memorial':
      return <Description color="primary" />
    case 'score':
      return <Assessment color="success" />
    case 'courtroom':
      return <VideoCall color="info" />
    case 'team':
      return <Groups color="secondary" />
    case 'evidence':
      return <AttachFile color="warning" />
    default:
      return <Notifications color="action" />
  }
}

// B.1 — small row component cho block "Phân tích & Báo cáo"
interface AnalyticsRowProps {
  icon: React.ReactNode
  label: string
  value?: number
  valueText?: string
}
function AnalyticsRow({ icon, label, value, valueText }: AnalyticsRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        py: 0.75,
        px: 1,
        borderRadius: 1.5,
        backgroundColor: (t) => alpha(t.palette.action.hover, 0.4),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700} color="primary.main">
        {valueText ?? value ?? 0}
      </Typography>
    </Box>
  )
}

export default function HomePage() {
  const { user } = useAppSelector((state) => state.auth)
  const isAdminQueue = useHasRole(['secretary', 'support'])
  const isStudent = useHasRole(['student'])
  const navigate = useNavigate()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: cases = [] } = useGetCasesQuery()
  const { data: dashboard } = useGetReportsDashboardQuery(undefined, {
    skip: !user || !isAdminQueue,
  })
  // B.1: data cho block "Phân tích & Báo cáo"
  const { data: allMemorials = [] } = useGetMemorialsQuery(
    {},
    { skip: !isAdminQueue },
  )
  // B.1: 4 thông báo mới nhất cho block "Thông báo" admin dashboard
  const { data: recentNotifs = [] } = useGetNotificationsQuery(
    { limit: 4 },
    { skip: !user, refetchOnMountOrArgChange: true },
  )
  const realStats = {
    totalCases: cases.length,
    totalTeams: teams.length,
    totalParticipants: teams.reduce((sum, t) => sum + (t.members?.filter(Boolean).length || 0), 0),
    totalCourtrooms: dashboard?.total_courtrooms ?? 0,
    submittedScoresCount: dashboard?.submitted_scores_count ?? 0,
    averageScore:
      dashboard?.average_total_score != null
        ? Number(dashboard.average_total_score.toFixed(2))
        : null,
  }
  const userTeam = teams.find((team) => team.members.includes(user?.id || ''))
  const userTeamSummary = userTeam ? getTeamRegistrationSummary(userTeam) : null
  const userTeamIssues = userTeam ? getPriorityRegistrationIssues(userTeam, 5) : []
  const canViewAdminQueue = isAdminQueue
  const teamsNeedingAction = teams.filter((team) => {
    const summary = getTeamRegistrationSummary(team)
    return team.status !== 'deleted' && (!summary.is_complete || team.status === 'registered' || team.status === 'rejected')
  })
  const missingStudentIdCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('missing_student_id')).length
  const missingProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('team_profile')).length
  const incompleteMembersCount = teams.filter((team) => getTeamRegistrationSummary(team).missing_keys.includes('member_count')).length
  const uploadedProfileCount = teams.filter((team) => getTeamRegistrationSummary(team).has_profile_file).length

  // ─── B.1 — Phân tích & Báo cáo (admin) ────────────────────────────────
  // Compute metrics chỉ từ data đã load — tránh thêm endpoint mới.
  const submittedMemorialTeamIds = new Set(
    allMemorials.filter((m: any) => m.team_id && m.is_official_submission).map((m: any) => m.team_id),
  )
  const teamsSubmittedR1Count = teams.filter(
    (t) => t.status !== 'deleted' && submittedMemorialTeamIds.has(t.id),
  ).length
  const teamsMissingDossierCount = teams.filter(
    (t) => t.status !== 'deleted' && !submittedMemorialTeamIds.has(t.id),
  ).length
  const teamsApprovedCount = teams.filter(
    (t) => t.status === 'active' && getTeamRegistrationSummary(t).is_complete,
  ).length
  // Đội vào shortlist (Top 4) — từ leaderboard, chỉ tính khi có đủ phiếu chấm.
  const shortlistCount = Math.min(4, (dashboard?.leaderboard || []).filter((r) => r.score_count > 0).length)
  // AI scoring status: placeholder — backend chưa expose; gợi ý dùng /scoring page.
  const aiScoringPending = (dashboard?.submitted_scores_count ?? 0) < (teams.length * (cases.length || 1))
  // Cảnh báo deadline: số cuộc thi có deadline trong 48h tới
  const now = new Date()
  const in48h = new Date(now.getTime() + 48 * 3600 * 1000)
  const upcomingDeadlineCount = cases.filter((c) => {
    if (!c.deadline) return false
    const dl = new Date(c.deadline)
    return dl > now && dl < in48h
  }).length

  // ─── B.2 — Student R1 result ──────────────────────────────────────────
  const studentFirstAssignedCaseId =
    isStudent && userTeam ? cases.find((c) => (c.assigned_team_ids || []).includes(userTeam.id))?.id : undefined
  const { data: latestPublicResult, isFetching: isFetchingPublicResult } = useGetPublicLatestResultsQuery(
    studentFirstAssignedCaseId || '',
    { skip: !studentFirstAssignedCaseId },
  )
  const r1Published = !!latestPublicResult?.published_at
  const isUserTeamInTop4 =
    r1Published && userTeam
      ? (latestPublicResult?.advancing_teams || []).some((at) => at.team_name === userTeam.name)
      : false

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Quản trị viên',
      secretary: 'Ban Thư ký',
      judge: 'Giám khảo',
      student: 'Thí sinh',
      support: 'Hỗ trợ kỹ thuật',
    }
    return labels[role] || role
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      {/* Welcome — thẻ hero đồng bộ nhận diện */}
      <Box
        sx={(theme) => ({
          mb: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: gradientBrand,
            borderRadius: '3px 0 0 3px',
          },
        })}
      >
        <Box sx={{ pl: { xs: 0.5, sm: 1 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.65rem', sm: '2.125rem' }, color: 'primary.main' }}>
            Chào mừng đến với E-Court Platform
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Xin chào, <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{user.full_name || user.username}</Box>
              </Typography>
              <Chip label={getRoleLabel(user.role)} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: '42rem', lineHeight: 1.65 }}>
            Hệ thống quản lý và tổ chức cuộc thi tranh tụng điện tử — giao diện thân thiện, luồng làm việc rõ ràng.
          </Typography>
        </Box>
      </Box>

      {userTeam && userTeamSummary && !userTeamSummary.is_complete && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2, alignItems: 'flex-start' }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/teams')}>
              Bổ sung
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Hồ sơ đội "{userTeam.name}" còn thiếu {userTeamSummary.missing_count} mục
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <LinearProgress
              variant="determinate"
              value={(userTeamSummary.required_done / Math.max(1, userTeamSummary.required_total)) * 100}
              color="warning"
              sx={{ maxWidth: 320, height: 7, borderRadius: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {userTeamIssues.map((item) => (
                <Chip key={item.key} label={item.label} color="warning" size="small" variant="outlined" />
              ))}
            </Box>
          </Box>
        </Alert>
      )}

      {canViewAdminQueue && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Hàng chờ duyệt hồ sơ đội
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teamsNeedingAction.length} đội cần kiểm tra, duyệt hoặc yêu cầu bổ sung.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Thiếu MSSV: ${missingStudentIdCount}`} color={missingStudentIdCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Thiếu hồ sơ đội: ${missingProfileCount}`} color={missingProfileCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Chưa đủ thành viên: ${incompleteMembersCount}`} color={incompleteMembersCount ? 'warning' : 'default'} size="small" />
              <Chip label={`Đã upload profile: ${uploadedProfileCount}`} color="success" variant="outlined" size="small" />
            </Box>
            <Button variant="outlined" size="small" onClick={() => navigate('/teams')} sx={{ textTransform: 'none' }}>
              Mở đội thi
            </Button>
          </Box>
        </Paper>
      )}

      {/* B.2 — Block ưu tiên cho Thí sinh: kết quả Vòng Sơ loại */}
      {isStudent && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Block 1: Thông báo kết quả Top 4 */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.12)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
              })}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}
              >
                <EmojiEvents /> Thông báo kết quả Top 4
              </Typography>
              {isFetchingPublicResult ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Đang tải...
                </Typography>
              ) : !r1Published ? (
                <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <HourglassEmpty color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Kết quả Vòng Sơ loại chưa được công bố
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ban Tổ chức sẽ thông báo Top 4 đội vào Vòng Bán kết khi có quyết định chính thức.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {latestPublicResult?.title || 'Kết quả Vòng Sơ loại đã được công bố.'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {(latestPublicResult?.advancing_teams || []).slice(0, 4).map((at, idx) => (
                      <Box
                        key={`${at.team_name}-${idx}`}
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: 1.5,
                          backgroundColor:
                            userTeam && at.team_name === userTeam.name
                              ? alpha(theme.palette.secondary.main, 0.16)
                              : alpha(theme.palette.action.hover, 0.4),
                          border:
                            userTeam && at.team_name === userTeam.name
                              ? `1px solid ${theme.palette.secondary.main}`
                              : '1px solid transparent',
                        })}
                      >
                        <Chip
                          label={idx + 1}
                          color="secondary"
                          size="small"
                          sx={{ fontWeight: 700, minWidth: 32 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {at.team_name}
                          </Typography>
                          {at.university && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {at.university}
                            </Typography>
                          )}
                        </Box>
                        {userTeam && at.team_name === userTeam.name && (
                          <Chip label="Đội của bạn" size="small" color="secondary" />
                        )}
                      </Box>
                    ))}
                  </Box>
                  <Button
                    fullWidth
                    variant="text"
                    color="secondary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/public/results')}
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Xem chi tiết
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Block 2: Kết quả đội của bạn sau Vòng Sơ loại */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                height: '100%',
                borderRadius: 3,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
              })}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
              >
                <TrendingUp /> Kết quả đội của bạn sau Vòng Sơ loại
              </Typography>
              {!userTeam ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }} fontStyle="italic">
                  Bạn chưa được phân vào đội thi nào.
                </Typography>
              ) : isFetchingPublicResult ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Đang tải...
                </Typography>
              ) : !r1Published ? (
                <Box sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <HourglassEmpty color="action" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Chưa có kết quả công bố
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Đội <strong>{userTeam.name}</strong> đang chờ Ban Tổ chức công bố kết quả Vòng Sơ loại.
                      Hãy theo dõi mục Thông báo để không bỏ lỡ.
                    </Typography>
                  </Box>
                </Box>
              ) : isUserTeamInTop4 ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <CheckCircle color="success" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="success.main">
                        Chúc mừng! Đội bạn vào Top 4
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userTeam.name} đã vượt qua Vòng Sơ loại — sẵn sàng cho Vòng Bán kết.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/courtroom')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Chuẩn bị Vòng Bán kết
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    <ErrorOutline color="warning" sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Đội bạn chưa vào Top 4
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cảm ơn {userTeam.name} đã tham dự Vòng Sơ loại. Bạn vẫn có thể xem kết quả
                        chung và phản hồi của Ban Giám khảo trong mục Thành tích.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/achievements')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Xem thành tích
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              borderTop: '4px solid',
              borderTopColor: 'primary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.primary.main, 0.12)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số cuộc thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {realStats.totalCases}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#144E8C', 0.12), color: 'primary.main', width: 56, height: 56 }}>
                  <Folder />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderTop: '4px solid',
              borderTopColor: 'secondary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.15)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số đội thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {realStats.totalTeams}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#F64183', 0.12), color: 'secondary.main', width: 56, height: 56 }}>
                  <Groups />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'success.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.success.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số thí sinh
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {realStats.totalParticipants}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.dark', width: 56, height: 56 }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.info.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Điểm trung bình
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {(realStats.averageScore ?? '—')}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.dark', width: 56, height: 56 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <ArrowForward color="secondary" /> Truy cập nhanh
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Folder />}
                onClick={() => navigate('/cases')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Cuộc thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Groups />}
                onClick={() => navigate('/teams')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Đội thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Description />}
                onClick={() => navigate('/memorials')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Memorial
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Assessment />}
                onClick={() => navigate('/scoring')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Chấm điểm
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<VideoCall />}
                onClick={() => navigate('/courtroom')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Virtual Courtroom
              </Button>
              {isAdminQueue && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<BarChart />}
                  onClick={() => navigate('/reports')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                >
                  Báo cáo và Phân tích
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* B.1 — Phân tích & Báo cáo (thay block "Hoạt động gần đây" cũ) */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <BarChart color="secondary" /> Phân tích & Báo cáo
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <AnalyticsRow
                icon={<CheckCircle fontSize="small" color="success" />}
                label="Đội đã nộp đủ Vòng Sơ loại"
                value={teamsSubmittedR1Count}
              />
              <AnalyticsRow
                icon={<ErrorOutline fontSize="small" color="warning" />}
                label="Đội thiếu hồ sơ"
                value={teamsMissingDossierCount}
              />
              <AnalyticsRow
                icon={<SmartToy fontSize="small" sx={{ color: aiScoringPending ? 'warning.main' : 'success.main' }} />}
                label="Trạng thái AI chấm"
                valueText={aiScoringPending ? 'Đang chạy' : 'Hoàn tất'}
              />
              <AnalyticsRow
                icon={<People fontSize="small" color="primary" />}
                label="Đội đã duyệt"
                value={teamsApprovedCount}
              />
              <AnalyticsRow
                icon={<EmojiEvents fontSize="small" sx={{ color: 'secondary.main' }} />}
                label="Đội vào shortlist (Top 4)"
                value={shortlistCount}
              />
              <AnalyticsRow
                icon={<WarningAmber fontSize="small" color="error" />}
                label="Cảnh báo deadline 48h"
                value={upcomingDeadlineCount}
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BarChart />}
              onClick={() => navigate('/reports')}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              Xem báo cáo chi tiết
            </Button>
          </Paper>
        </Grid>

        {/* Upcoming Courtrooms & Top Teams */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* T-7 countdown widget — phiên Bán kết / Chung kết tiếp theo */}
            <NextHearingCountdown />

            {/* Upcoming Courtrooms */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <Schedule color="secondary" /> Sắp diễn ra
              </Typography>
              <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                Lịch phiên Bán kết / Chung kết hiển thị qua widget T-7 ở trên — countdown
                tự động theo dữ liệu thực tế của cuộc thi bạn được phân công.
              </Typography>
              <Button
                fullWidth
                variant="text"
                color="primary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/courtroom')}
                sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Xem tất cả
              </Button>
            </Paper>

            {/* B.1 — Thông báo (thay block "Top 3 đội thi" cũ) */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <Notifications color="secondary" /> Thông báo
              </Typography>
              {recentNotifs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ py: 1 }}>
                  Chưa có thông báo nào.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {recentNotifs.slice(0, 4).map((n) => (
                    <ListItem
                      key={n.id}
                      disableGutters
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => navigate('/notifications')}
                    >
                      <ListItemAvatar sx={{ minWidth: 36, mt: 0.5 }}>
                        {getActivityIcon(n.metadata?.kind || n.type || 'info')}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                            {n.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {n.message}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              <Button
                fullWidth
                variant="text"
                color="primary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/notifications')}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
              >
                Xem tất cả
              </Button>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}
